import React, { useState, useEffect, useCallback } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import Box from '@mui/material/Box';
import { useVersionCheck } from './Hooks/useVersionCheck';

/**
 * Clés localStorage utilisées pour piloter la mise à jour à travers le rechargement de la page.
 * Le state React étant réinitialisé au reload, on s'appuie sur le localStorage qui lui survit.
 */
export const UPDATE_RELOAD_PENDING_KEY = 'app-update-reload-pending';
export const UPDATE_JUST_UPDATED_KEY = 'app-update-just-updated';

const AUTO_RELOAD_DELAY = 15;

/**
 * Composant qui affiche une modale BLOQUANTE lorsqu'une nouvelle version est disponible.
 * L'utilisateur ne peut pas ignorer la mise à jour : un compte à rebours déclenche
 * automatiquement le rechargement, qui forcera ensuite la déconnexion (cf. App.js)
 * afin d'obtenir un nouveau token à jour.
 */
export default function UpdateNotification() {
    const { isNewVersionAvailable, currentVersion, latestVersion } = useVersionCheck();
    const [open, setOpen] = useState(false);
    const [autoReloadCountdown, setAutoReloadCountdown] = useState(AUTO_RELOAD_DELAY);

    const handleReload = useCallback(() => {
        // Mémoriser la nouvelle version et armer les flags qui survivront au rechargement.
        localStorage.setItem('app-version', latestVersion);
        localStorage.setItem(UPDATE_RELOAD_PENDING_KEY, 'true');
        localStorage.setItem(UPDATE_JUST_UPDATED_KEY, latestVersion || 'true');

        // Vider le cache pour garantir le chargement de la nouvelle version.
        if ('caches' in window) {
            caches.keys().then((names) => {
                names.forEach((name) => {
                    caches.delete(name);
                });
            });
        }

        // Rechargement forcé (hard reload).
        window.location.reload(true);
    }, [latestVersion]);

    useEffect(() => {
        if (isNewVersionAvailable) {
            setOpen(true);
        }
    }, [isNewVersionAvailable]);

    // Compte à rebours pour le rechargement automatique.
    useEffect(() => {
        if (open && isNewVersionAvailable) {
            const timer = setInterval(() => {
                setAutoReloadCountdown((prev) => {
                    if (prev <= 1) {
                        clearInterval(timer);
                        handleReload();
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);

            return () => clearInterval(timer);
        }
    }, [open, isNewVersionAvailable, handleReload]);

    if (!isNewVersionAvailable) {
        return null;
    }

    const progress = ((AUTO_RELOAD_DELAY - autoReloadCountdown) / AUTO_RELOAD_DELAY) * 100;

    return (
        <Dialog
            open={open}
            // Modale bloquante : pas de fermeture au clic extérieur ni à la touche Échap.
            disableEscapeKeyDown
            onClose={(event, reason) => {
                // On ignore explicitement toute tentative de fermeture.
                return;
            }}
            maxWidth="xs"
            fullWidth
        >
            <DialogTitle sx={{ fontWeight: 'bold' }}>
                Nouvelle version disponible
            </DialogTitle>
            <DialogContent>
                <DialogContentText component="div">
                    Une nouvelle version de l'application est disponible. Pour bénéficier
                    des dernières mises à jour, l'application va se recharger et vous
                    devrez vous reconnecter.
                    <Box sx={{ mt: 2, fontSize: '0.85em', opacity: 0.8 }}>
                        Version actuelle : {currentVersion} → Nouvelle version : {latestVersion}
                    </Box>
                    <Box sx={{ mt: 2 }}>
                        Rechargement automatique dans <strong>{autoReloadCountdown}</strong> seconde{autoReloadCountdown > 1 ? 's' : ''}...
                    </Box>
                    <LinearProgress variant="determinate" value={progress} sx={{ mt: 1 }} />
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleReload}
                    autoFocus
                >
                    Recharger maintenant
                </Button>
            </DialogActions>
        </Dialog>
    );
}
